import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { Location } from '../location/location.model';
import {
  BulkLocationApi,
  BulkLocationFailureApi,
  BulkLocationSuccessApi,
  CreateLocationApi,
  CreateLocationFailureApi,
  CreateLocationSuccessApi,
  DeleteLocationApi,
  DeleteLocationFailureApi,
  DeleteLocationSuccessApi,
  LoadLocationApi,
  LoadLocationFailureApi,
  LoadLocationsApi,
  LoadLocationsFailureApi,
  LoadLocationsSuccessApi,
  LoadLocationSuccessApi,
  LocationApiActionTypes,
  UpdateLocationApi,
  UpdateLocationFailureApi,
  UpdateLocationStatusApi,
  UpdateLocationStatusFailureApi,
  UpdateLocationStatusSuccessApi,
  UpdateLocationSuccessApi,
} from './location-api.actions';
import { LocationApiService } from './location-api.service';

@Injectable()
export class LocationApiEffects {
  loadLocationsApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.LoadLocationsApi),
      switchMap(({ payload }: LoadLocationsApi) =>
        this.srv.get(payload.params).pipe(
          map((locations: Location[]) => new LoadLocationsSuccessApi({ locations })),
          catchError((err) => of(new LoadLocationsFailureApi(err)))
        )
      )
    )
  );

  loadOneLocationsApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.LoadLocationApi),
      switchMap(({ payload: { id } }: LoadLocationApi) =>
        this.srv.getOne(id).pipe(
          map((location) => new LoadLocationSuccessApi({ location })),
          catchError(({ message }) => of(new LoadLocationFailureApi({ id, message })))
        )
      )
    )
  );

  createLocationApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.CreateLocationApi),
      mergeMap(({ payload }: CreateLocationApi) =>
        this.srv.create(payload.location).pipe(
          map((location) => new CreateLocationSuccessApi({ location })),
          catchError((error) => of(new CreateLocationFailureApi(error)))
        )
      )
    )
  );

  updateLocationApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.UpdateLocationApi),
      mergeMap(({ payload }: UpdateLocationApi) =>
        this.srv.update(payload.location).pipe(
          map((location) => new UpdateLocationSuccessApi({ location })),
          catchError((error) => of(new UpdateLocationFailureApi(error)))
        )
      )
    )
  );

  UpdateLocationStatusApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.UpdateLocationStatusApi),
      mergeMap(({ payload }: UpdateLocationStatusApi) =>
        this.srv.updateLocationStatus(payload.location).pipe(
          map((location) => new UpdateLocationStatusSuccessApi({ location })),
          catchError((error) => of(new UpdateLocationStatusFailureApi(error)))
        )
      )
    )
  );

  deleteLocationApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.DeleteLocationApi),
      mergeMap(({ payload }: DeleteLocationApi) =>
        this.srv.delete(payload.id).pipe(
          map(({ id }) => new DeleteLocationSuccessApi({ id: payload.id })),
          catchError((err) => of(new DeleteLocationFailureApi(err)))
        )
      )
    )
  );

  bulkSensorApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationApiActionTypes.BulkLocationApi),
      mergeMap(({ payload }: BulkLocationApi) =>
        this.srv.bulk(payload).pipe(
          map(() => new BulkLocationSuccessApi({ correlation_id: payload.correlation_id })),
          catchError((err) =>
            of(
              new BulkLocationFailureApi({
                ...err,
                correlation_id: payload.correlation_id,
              })
            )
          )
        )
      )
    )
  );

  constructor(private actions$: Actions, private srv: LocationApiService) {}
}
